// src/admin/components/reports/widgets/OccupancyWidget.jsx
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { getOccupancyStats } from '../../../../models/reportModel';
import { RiAlertLine, RiRefreshLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

const OccupancyWidget = ({ isDarkMode }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getOccupancyStats();
        setData(response);
      } catch (err) {
        console.error(t('error_fetching_occupancy'), err);
        setError(err.message || t('failed_load_occupancy'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [t]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 border-red-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
      } border flex items-center h-64 justify-center`}>
        <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
        {error}
      </div>
    );
  }
  
  if (!data) return null;
  
  const { summary } = data;
  
  const chartData = [
    { name: t('occupied'), value: summary.occupiedApartments },
    { name: t('vacant'), value: summary.vacantApartments },
    { name: t('maintenance'), value: summary.maintenanceApartments }
  ];
  
  const COLORS = ['#10b981', '#3b82f6', '#f59e0b'];
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / summary.totalApartments) * 100).toFixed(1);
      
      return (
        <div className={`p-3 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 text-white' : 'bg-white text-gray-900'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-sm mt-1">
            {payload[0].value} {t('apartments')} ({percentage}%)
          </p>
        </div>
      );
    }
    
    return null;
  };

  const getStatusLabel = (rate) => {
    if (rate > 90) return t('excellent');
    if (rate > 75) return t('good');
    return t('needs_improvement');
  };

  const getStatusColor = (rate) => {
    if (rate > 90) return isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700';
    if (rate > 75) return isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-700';
    return isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-700';
  };
  
  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <div className="mb-4">
        <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h4 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {summary.overallOccupancyRate}%
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
              {summary.occupiedApartments} {t('of')} {summary.totalApartments} {t('apartments')}
            </p>
          </div>
          <div className={`p-2 rounded-lg text-xs font-medium ${getStatusColor(summary.overallOccupancyRate)}`}>
            {getStatusLabel(summary.overallOccupancyRate)}
          </div>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36} 
            wrapperStyle={{ 
              color: isDarkMode ? '#e5e7eb' : '#1f2937',
              textAlign: isRTL ? 'right' : 'left'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OccupancyWidget;